/**
 * In-memory wizard-hook registry.
 *
 * Plugins call `registerWizardHook(hook)` at boot from
 * `wizards/register.ts`. The `WizardChain` controller calls
 * `getWizardHooksFor(kind)` each render to pull the current ordered
 * list of hooks for an event.
 *
 * The registry is process-global because the SPA is one bundle —
 * once dynamic plugin loading lands, plugin bundles will call the
 * same `registerWizardHook` from their own entry points and the
 * controller picks them up without changes.
 *
 * Dedup: hot module replacement during dev can call register multiple
 * times for the same hook id; we replace by id rather than push so
 * we don't end up with duplicates in the dropdown after a save.
 */
import type { WizardEventKind, WizardHook } from './types.js';

const hooks: WizardHook[] = [];

/**
 * Register a wizard hook. Hooks are deduped by id — re-registering
 * an existing id replaces the previous entry (useful for HMR).
 */
export function registerWizardHook<K extends WizardEventKind>(hook: WizardHook<K>): void {
  // Cast through `unknown` — the registry stores existentially-quantified
  // hooks (the K is "some kind"), but TS's variance check on render's
  // contravariant `props` parameter can't see that storing a covariant
  // K is safe. The runtime is fine; we lose K when we put it in the
  // array, get it back via the assertion in getWizardHooksFor.
  const stored = hook as unknown as WizardHook;
  const existingIdx = hooks.findIndex((h) => h.id === hook.id);
  if (existingIdx >= 0) {
    hooks[existingIdx] = stored;
    return;
  }
  hooks.push(stored);
}

/**
 * Get all registered hooks for an event, sorted by `order` ascending.
 * Returns a fresh array each call — callers can mutate freely.
 *
 * The internal store is `WizardHook[]` (existential `K`); we cast the
 * filtered result to the caller's specific `K`. The filter predicate
 * guarantees the runtime safety; the cast just appeases TS's
 * conservative variance check on the type guard.
 */
export function getWizardHooksFor<K extends WizardEventKind>(kind: K): Array<WizardHook<K>> {
  // Cast via unknown because the generic K is a subtype of the stored
  // existential WizardEventKind — TS's conservative variance check
  // can't see that the filter guarantees the narrowing at runtime.
  return (hooks.filter((h) => h.eventKind === kind) as unknown as Array<WizardHook<K>>).sort(
    (a, b) => a.order - b.order,
  );
}

/**
 * Test-only — wipes the registry. Real callers should not need this;
 * `registerWizardHook` already dedups by id.
 */
export function _resetWizardHookRegistry(): void {
  hooks.length = 0;
}
